import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  InputBase,
  Badge,
  Button,
  Divider,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuth } from '../../hooks/useAuth';
import { LoginDialog } from '../Auth/LoginDialog';

export const Header = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { items } = useCart();
  const { isAuthenticated, user, logout } = useAuth();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const [loginOpen, setLoginOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (!debouncedSearchQuery && searchParams.get('search')) {
      const params = new URLSearchParams(window.location.search);
      params.delete('search');
      if (params.toString()) {
        navigate(`?${params.toString()}`, { replace: true });
      } else {
        navigate('/products', { replace: true });
      }
    } else if (debouncedSearchQuery && debouncedSearchQuery.length >= 3) {
      const params = new URLSearchParams(window.location.search);
      params.set('search', debouncedSearchQuery.trim());
      if (window.location.pathname !== '/products') {
        navigate(`/products?${params.toString()}`);
      } else {
        navigate(`?${params.toString()}`, { replace: true });
      }
    }
  }, [debouncedSearchQuery, navigate, searchParams]);

  return (
    <AppBar position="sticky">
      <Toolbar>
        <Typography
          variant="h6"
          noWrap
          onClick={() => navigate('/')}
          sx={{
            display: { xs: 'none', sm: 'block' },
            textDecoration: 'none',
            color: 'inherit',
            mr: 2,
            cursor: 'pointer'
          }}
        >
          Digital StoreFront
        </Typography>

        {/* Left group: logo only */}
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
          {/* No All Products button */}
        </Box>

        {/* Right group: About, Policy, Contact, Search, Cart, Login */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 }, minWidth: 0 }}>
          {/* Static page links: right side on desktop, inline on mobile */}
          <Button
            color="inherit"
            onClick={() => navigate('/about')}
            sx={{ fontSize: { xs: '0.85rem', md: '1rem' }, px: { xs: 0.5, md: 2 }, minWidth: { xs: 0, sm: 40 } }}
          >
            About
          </Button>
          <Button
            color="inherit"
            onClick={() => navigate('/contact')}
            sx={{ fontSize: { xs: '0.85rem', md: '1rem' }, px: { xs: 0.5, md: 2 }, minWidth: { xs: 0, sm: 40 } }}
          >
            Contact
          </Button>
          <Button
            color="inherit"
            onClick={() => navigate('/policy')}
            sx={{ fontSize: { xs: '0.85rem', md: '1rem' }, px: { xs: 0.5, md: 2 }, minWidth: { xs: 0, sm: 40 } }}
          >
            Policy
          </Button>

          {/* Search bar and login/cart: group for better alignment */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
            <Box sx={{ position: 'relative', width: searchOpen ? { xs: 68, sm: 110, md: 200 } : 36, minWidth: 0, transition: 'width 0.3s' }}>
              <IconButton
                onClick={() => setSearchOpen((open) => !open)}
                sx={{ p: 0.5, position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
              >
                <SearchIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </IconButton>
              {searchOpen && (
                <InputBase
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search"
                  sx={{
                    color: 'inherit',
                    fontSize: { xs: '0.8rem', sm: '1rem' },
                    padding: (theme) => theme.spacing(0.75, 1, 0.75, 0),
                    pl: 4.5,
                    width: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    },
                    minWidth: 0,
                    marginLeft: '-8px',
                  }}
                  inputProps={{ style: { padding: 0, minWidth: 0 } }}
                  onBlur={() => setSearchOpen(false)}
                />
              )}
            </Box>

            <IconButton
              size="large"
              color="inherit"
              onClick={() => navigate('/cart')}
              sx={{ ml: { xs: 0, sm: 1 }, display: { xs: 'none', sm: 'inline-flex' } }}
            >
              <Badge badgeContent={items.length} color="error">
                <ShoppingCartIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </Badge>
            </IconButton>

            <Divider orientation="vertical" flexItem sx={{ mx: { xs: 0.5, sm: 1 }, bgcolor: 'rgba(255, 255, 255, 0.2)', display: { xs: 'none', sm: 'block' } }} />

            {isAuthenticated && user && user.full_name ? (
  <>
    <Button
      color="inherit"
      onClick={(e) => setUserMenuAnchor(e.currentTarget)}
      startIcon={<PersonIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />}
      sx={{
        fontSize: { xs: '0.85rem', sm: '1rem' },
        minWidth: { xs: 0, sm: 40 },
        px: { xs: 0.5, sm: 2 },
        ml: { xs: 0, sm: 1 },
        alignSelf: { xs: 'center', sm: 'auto' },
        height: { xs: 32, sm: 36 },
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        textTransform: 'none',
        fontWeight: 500,
        color: 'inherit',
        background: 'none',
        border: 0,
      }}
    >
      {user.full_name}
    </Button>
    <Menu
      anchorEl={userMenuAnchor}
      open={Boolean(userMenuAnchor)}
      onClose={() => setUserMenuAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <MenuItem
        onClick={() => {
          navigate('/orders');
          setUserMenuAnchor(null);
        }}
      >
        Order History
      </MenuItem>
      <MenuItem
        onClick={() => {
          logout();
          setUserMenuAnchor(null);
        }}
      >
        Logout
      </MenuItem>
    </Menu>
  </>
) : (
              <Button
                color="inherit"
                onClick={() => setLoginOpen(true)}
                startIcon={<PersonIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />}
                sx={{
                  fontSize: { xs: '0.85rem', sm: '1rem' },
                  minWidth: { xs: 0, sm: 40 },
                  px: { xs: 0.5, sm: 2 },
                  ml: { xs: 0, sm: 1 },
                  alignSelf: { xs: 'center', sm: 'auto' },
                  height: { xs: 32, sm: 36 },
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <span style={{ display: 'none' }} className="login-label-sm">Login</span>
                <span style={{ display: 'inline' }} className="login-label-md">Login</span>
              </Button>
            )}
          </Box>
        </Box>
      </Toolbar>

      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
    </AppBar>
  );
}

/* Add to the bottom of the file for responsive Login text */
/*
// In your CSS (e.g., index.css or Header.module.css):
.login-label-sm { display: inline; }
.login-label-md { display: none; }
@media (min-width:600px) {
  .login-label-sm { display: none !important; }
  .login-label-md { display: inline !important; }
}
*/
